import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CERecommendedHrsResponse, } from '../../../common/Contracts';
import { RootState } from '../app.store';
import { Constants } from '../../../common/constants';
import { isNullOrEmpty } from '../../../Utils/StringUtils';
import { format, parse, parseISO } from 'date-fns';
import moment from 'moment';

export interface AssignmentDetails extends CERecommendedHrsResponse {
}
export interface AssignmentDetailsState {
  isLoading: boolean;
  assignmentDetails: AssignmentDetails | null;
}
const assignmentDetailsSlice = createSlice({
  name: 'assignmentDetails',
  initialState: {
    isLoading: false,
    assignmentDetails: null as AssignmentDetails | null,
  },
  reducers: {
    RA_ASSIGMENT_DETAILS_REDUCER: (
      state,
      action: PayloadAction<AssignmentDetails>
    ) => {
      state.assignmentDetails = action.payload;
    },
    RA_FETCH_ASSIGNMENTS_INITIATE: state => {
      state.isLoading = true;
    },
    RA_FETCH_ASSIGNMENTS_SUCCESS: state => {
      state.isLoading = false;
    },
    RA_FETCH_ASSIGNMENTS_FAILURE: state => {
      state.isLoading = false;
    },
  },
});

export const {
  RA_ASSIGMENT_DETAILS_REDUCER,
  RA_FETCH_ASSIGNMENTS_INITIATE,
  RA_FETCH_ASSIGNMENTS_SUCCESS,
  RA_FETCH_ASSIGNMENTS_FAILURE,
} = assignmentDetailsSlice.actions;

export default assignmentDetailsSlice.reducer;
export const assignmentDetailsSelector = (state: RootState) =>
  state.assignments.assignmentDetails;
export const isAssignmentDetailsLoadedSelector = (state: RootState) => state.assignments.isLoading;
export const assignmentDetailsSelectorById =
  (requestId: number) => (state: RootState) =>
    state.assignments.assignmentDetails.eventDetails.find(e => e.customProperties.ResourceRequestId === requestId);
export const npDetailsSelectorById = (key: string) => (state: RootState) =>
  state.assignments.assignmentDetails.eventDetails.find(
    e => e.customProperties?.EventTypeId ? e.customProperties?.EventTypeId?.toString() === key : e.eventCategory === key
  );

export const assignmentForecastHoursTransformDataSelector = (state: RootState) => {
  let dailyGrainDateList = state.dateNavigation.dailyGrainDateList;
  const projectEvents = state.assignments.assignmentDetails?.eventDetails?.filter(event => event.eventCategory.toLowerCase() === "project");
  const transformedData: Record<number, any> = {};
  let currentRowIndex = 0;
  projectEvents?.forEach((project) => {
    const requestIdIndex = parseInt(project.customProperties?.ResourceRequestId);
    if (!transformedData[requestIdIndex]) {
      transformedData[requestIdIndex] = {
        rowId: project.customProperties?.ResourceRequestId,
        rowIndex: currentRowIndex,
        rowType: 'Assignment',
        rowData: [],
      };
      currentRowIndex += 1;
    }
    let schedules = project.customProperties?.Schedules;
    dailyGrainDateList.forEach(date => {
      const scheduleOnADay = schedules.filter((schedule: any) => {
        const dateMoment = moment(schedule.forecastDate).format(Constants.DATE_FORMAT);
        return dateMoment === date
      });
      // If no schedule found for the date, set default value to 0
      transformedData[requestIdIndex].rowData.push({
        columnId: date,
        columnIndex: transformedData[requestIdIndex].rowData.length,
        columnData: {
          primaryValue: scheduleOnADay.length === 0 ? 0 : scheduleOnADay[0].durationInHours,
          additionalDetails: "",
          lastUpdatedBy: "",
        },
      })
    });
    //Sort the columns by date
    transformedData[requestIdIndex].rowData.sort((a: any, b: any) => {
      const dateA = new Date(a.columnId).getTime();
      const dateB = new Date(b.columnId).getTime();
      return dateA - dateB;
    });
    // Assign column index based on the sorted order
    transformedData[requestIdIndex].rowData.forEach((column: any, columnIndex: any) => {
      column.columnIndex = columnIndex;
    });
  });
  // Convert the object back to an array and sort based on rowIndex
  let resultArray = Object.values(transformedData).sort((a, b) => a.rowIndex - b.rowIndex);

  resultArray = resultArray.filter((row: any) => {
    const assignmentIndex = projectEvents.findIndex((assignment: any) => assignment.customProperties.ResourceRequestId === row.rowId);
    if (assignmentIndex == -1) return false;
    else return true;
  });
  return resultArray;
}
export const nonProjectDetailsTransformDataSelector = (state: RootState) => {
  let dailyGrainDateList = state.dateNavigation.dailyGrainDateList;
  const nonProjectEvents = state.assignments.assignmentDetails?.eventDetails?.filter(event => {
    const category = event.eventCategory?.toLowerCase();
    return category === 'msvacation' || category === 'nonproject' || category === 'publicholiday';
  }) || [];
  const outlookEvents = state.assignments.assignmentDetails?.outlookCalendarDetails;
  const transformedData: Record<string, any> = {};
  let currentRowIndex = 0;
  nonProjectEvents.forEach((nonProject) => {
    const requestIdIndex = nonProject.customProperties?.EventTypeId? nonProject.customProperties.EventTypeId : nonProject.eventCategory;
    const requestKey = String(requestIdIndex);
    if (!transformedData[requestKey]) {
      const isPublicHoliday = (
        nonProject.eventCategory?.toLowerCase() === 'publicholiday' ||
        nonProject.customProperties?.EventTypeName?.toLowerCase() === 'public holiday' ||
        nonProject.customProperties?.AssignmentType?.toLowerCase() === 'public holiday' ||
        nonProject.customProperties?.EventType?.toLowerCase() === 'public holiday' ||
        nonProject.customProperties?.eventType?.toLowerCase() === 'public holiday'
      );
      transformedData[requestKey] = {
        rowId: requestIdIndex,
        rowIndex: currentRowIndex,
        rowType: isPublicHoliday ? 'PublicHoliday' : typeof requestIdIndex === 'number' ? requestIdIndex === 35 ? 'Vacation' : 'NonProject' : requestIdIndex,
        rowData: [],
      };
      currentRowIndex += 1;
    }
    let npDate = moment(nonProject.startDateTime).format(Constants.DATE_FORMAT);
    if (transformedData[requestKey].rowData.length === 0) {
      dailyGrainDateList.forEach(date => {
        const scheduleOnADay = npDate === date ? (new Date(nonProject.endDateTime).getTime() - new Date(nonProject.startDateTime).getTime()) / (1000 * 60 * 60) : 0;
        // If no schedule found for the date, set default value to 0
        transformedData[requestKey].rowData.push({
          columnId: date,
          columnIndex: transformedData[requestKey].rowData.length,
          columnData: {
            primaryValue: scheduleOnADay,
            additionalDetails: "",
            lastUpdatedBy: "",
          },
        });
      })
    } else {
      const col = transformedData[requestKey].rowData.find((c: any) => c.columnId === npDate);
      if (col) {
        col.columnData.primaryValue += (new Date(nonProject.endDateTime).getTime() - new Date(nonProject.startDateTime).getTime()) / (1000 * 60 * 60);
      }      
    }
    //Sort the columns by date
    transformedData[requestKey].rowData.sort((a: any, b: any) => {
      const dateA = new Date(a.columnId).getTime();
      const dateB = new Date(b.columnId).getTime();
      return dateA - dateB;
    });
    // Assign column index based on the sorted order
    transformedData[requestKey].rowData.forEach((column: any, columnIndex: any) => {
      column.columnIndex = columnIndex;
    });
  });
  dailyGrainDateList.forEach(date => {
    const requestIdIndex = "Outlook";
    if (!transformedData[requestIdIndex]) {
      transformedData[requestIdIndex] = {
        rowId: requestIdIndex,
        rowIndex: currentRowIndex,
        rowType: 'Outlook',
        rowData: [],
      };
      currentRowIndex += 1;
    }
    let outlookBusyHrs = 0;
    if (outlookEvents?.isBusinessHoursEnabled)
    {
      const businessStartTime = parse(outlookEvents.workPreferences?.startTime, "HH:mm:ss", new Date(date));
      const businessEndTime = parse(outlookEvents.workPreferences?.endTime, "HH:mm:ss", new Date(date));
      let outlookBusyBlocks = outlookEvents.eventDetails.reduce((list, event) => {
        // Convert event start/end to Date
        const eventStart = new Date(event.startDateTime);
        const eventEnd = new Date(event.endDateTime);
        if ((event.status.toLowerCase() === 'busy' || event.status.toLowerCase() === 'oof') && 
          eventEnd > businessStartTime && eventStart < businessEndTime) {
          // Clamp event times to business hours
          const start = eventStart < businessStartTime ? businessStartTime : eventStart;
          const end = eventEnd > businessEndTime ? businessEndTime : eventEnd;
          list.push({ startTime: start, endTime: end });
        }
        return list;
      }, []);
      // Merge overlapping time blocks
      outlookBusyBlocks.sort((a, b) => a.startTime - b.startTime);
      const mergedBlocks = [];
      for (const block of outlookBusyBlocks) {
        if (mergedBlocks.length === 0) {
          mergedBlocks.push(block);
        } else {
          const last = mergedBlocks[mergedBlocks.length - 1];
          if (block.startTime <= last.endTime) {
            // Overlapping, merge
            last.endTime = new Date(Math.max(last.endTime.getTime(), block.endTime.getTime()));
          } else {
            mergedBlocks.push(block);
          }
        }
      }
      // Sum the merged blocks
      outlookBusyHrs = mergedBlocks.reduce((sum, block) => {
        return sum + (block.endTime.getTime() - block.startTime.getTime()) / (1000 * 60 * 60);
      }, 0);
    }
    else {
      const scheduleForDay = outlookEvents?.dayWiseAvailability[format(parseISO(date), "yyyy-MM-dd'T'00:00:00")];
      const busyHrs = ((scheduleForDay?.match(/2/g) || []).length * 30) / 60;
      const oofHrs = ((scheduleForDay?.match(/3/g) || []).length * 30) / 60;
      outlookBusyHrs = busyHrs + oofHrs;
    }      
    transformedData[requestIdIndex].rowData.push({
      columnId: date,
      columnIndex: transformedData[requestIdIndex].rowData.length,
      columnData: {
        primaryValue: Math.round(outlookBusyHrs * 100) / 100, // Round to 2 decimal places
        additionalDetails: "",
        lastUpdatedBy: "",
      },
    });
    //Sort the columns by date
    transformedData[requestIdIndex].rowData.sort((a: any, b: any) => {
      const dateA = new Date(a.columnId).getTime();
      const dateB = new Date(b.columnId).getTime();
      return dateA - dateB;
    });
    // Assign column index based on the sorted order
    transformedData[requestIdIndex].rowData.forEach((column: any, columnIndex: any) => {
      column.columnIndex = columnIndex;
    });
  });
  // Convert the object back to an array and sort based on rowIndex
  let resultArray = Object.values(transformedData).sort((a, b) => a.rowIndex - b.rowIndex);
  resultArray = resultArray.filter((row: any) => {
    if (row.rowId === "Outlook") return true;
    const assignmentIndex = nonProjectEvents.findIndex((nonProject: any) => nonProject.eventCategory?.toLowerCase() === 'nonproject' ? String(nonProject.customProperties.Id)
      : nonProject.eventCategory === row.rowId);
    if (assignmentIndex == -1) return false;
    else return true;
  });
  return resultArray;
}
export const projectsAggregatedDataSelector = (state: RootState) => {
  const aggregatedData: Record<string, number> = {};
  const assignmentDetails = state.assignments.assignmentDetails?.eventDetails?.filter(event => event.eventCategory.toLowerCase() === "project");
  const dailyGrainDateList = state.dateNavigation?.dailyGrainDateList;
  const gridCellDetails = state.gridCell;
  dailyGrainDateList.forEach((date: string) => {
    aggregatedData[date] = 0;
  });
  assignmentDetails?.forEach((assignment) => {
    const customProperties = assignment?.customProperties;
    const schedules = customProperties?.Schedules;
    schedules?.forEach((schedule: any) => {
      const gridCell = gridCellDetails.find(cell => cell.rowId === customProperties.ResourceRequestId.toString() && cell.columnId === moment(schedule.forecastDate).format(Constants.DATE_FORMAT));
      const date = moment(schedule.forecastDate).format(Constants.DATE_FORMAT);
      if (aggregatedData.hasOwnProperty(date)) {
        aggregatedData[date] += gridCell? gridCell.currValue:schedule.durationInHours;
      }
    });
  });
  return aggregatedData;
}
export const nonProjectsAggregatedDataSelector = (state: RootState) => {
  const aggregatedData: Record<string, number> = {};
  const nonProjectEvents = state.assignments.assignmentDetails?.eventDetails?.filter(event => {
    const category = event.eventCategory?.toLowerCase();
    return category === 'msvacation' || category === 'nonproject' || category === 'publicholiday';
  }) || [];
  const dailyGrainDateList = state.dateNavigation?.dailyGrainDateList;
  dailyGrainDateList.forEach((date: string) => {
    aggregatedData[date] = 0;
  });
  nonProjectEvents?.forEach((nonProject) => {
    const npDate = moment(nonProject?.startDateTime).format(Constants.DATE_FORMAT);
    if (aggregatedData.hasOwnProperty(npDate)) {
      aggregatedData[npDate] += (new Date(nonProject?.endDateTime).getTime() - new Date(nonProject?.startDateTime).getTime()) / (1000 * 60 * 60);
    }
  });
  return aggregatedData;
}

